/// <amd-module name="Repos/Views/Branches/RestClient/Git" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\code\vss-code-web\repos\views\branches\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import * as Git from "Repos/Common/Contracts/Git";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export interface IGitRestClient extends IVssRestClient {
    /**
     * Retrieve statistics for multiple commits
     * 
     * @param searchCriteria - Base Commit and List of Target Commits to compare.
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     */
    getBranchStatsBatch(searchCriteria: Git.GitQueryBranchStatsCriteria, repositoryId: string, project?: string): Promise<Git.GitBranchStats[]>;
    /**
     * Retrieve a particular commit.
     * 
     * @param commitId - The id of the commit.
     * @param repositoryId - The id or friendly name of the repository. To use the friendly name, projectId must also be specified.
     * @param project - Project ID or project name
     * @param changeCount - The number of changes to include in the result.
     */
    getCommit(commitId: string, repositoryId: string, project?: string, changeCount?: number): Promise<Git.GitCommit>;
    /**
     * Retrieves pushes associated with the specified repository.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param skip - Number of pushes to skip.
     * @param top - Number of pushes to return.
     * @param searchCriteria - Search criteria attributes: fromDate, toDate, pusherId, refName, includeRefUpdates or includeLinks. fromDate: Start date to search from. toDate: End date to search to. pusherId: Identity of the person who submitted the push. refName: Branch name to consider. includeRefUpdates: If true, include the list of refs that were updated by the push. includeLinks: Whether to include the _links field on the shallow references.
     */
    getPushes(repositoryId: string, project?: string, skip?: number, top?: number, searchCriteria?: Git.GitPushSearchCriteria): Promise<Git.GitPush[]>;
    /**
     * Queries the provided repository for its refs and returns them.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param filter - [optional] A filter to apply to the refs (starts with).
     * @param includeLinks - [optional] Specifies if referenceLinks should be included in the result. default is false.
     * @param includeStatuses - [optional] Includes up to the first 1000 commit statuses for each ref. The default value is false.
     * @param includeMyBranches - [optional] Includes only branches that the user owns, the branches the user favorites, and the default branch. The default value is false. Cannot be combined with the filter parameter.
     * @param latestStatusesOnly - [optional] True to include only the tip commit status for each ref. This option requires \`includeStatuses\` to be true. The default value is false.
     * @param peelTags - [optional] Annotated tags will populate the PeeledObjectId property. default is false.
     * @param filterContains - [optional] A filter to apply to the refs (contains).
     * @param top - [optional] Maximum number of refs to return. It cannot be bigger than 1000. If it is not provided but continuationToken is, top will default to 100.
     * @param continuationToken - The continuation token used for pagination.
     */
    getRefs(repositoryId: string, project?: string, filter?: string, includeLinks?: boolean, includeStatuses?: boolean, includeMyBranches?: boolean, latestStatusesOnly?: boolean, peelTags?: boolean, filterContains?: string, top?: number, continuationToken?: string): Promise<VS_Services.PagedList<Git.GitRef>>;
    /**
     * Lock or Unlock a branch.
     * 
     * @param newRefInfo - The ref update action (lock/unlock) to perform
     * @param repositoryId - The name or ID of the repository.
     * @param filter - The name of the branch to lock/unlock
     * @param project - Project ID or project name
     * @param projectId - ID or name of the team project. Optional if specifying an ID for repository.
     */
    updateRef(newRefInfo: Git.GitRefUpdate, repositoryId: string, filter: string, project?: string, projectId?: string): Promise<Git.GitRef>;
    /**
     * Creating, updating, or deleting refs(branches).
     * 
     * @param refUpdates - List of ref updates to attempt to perform
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param projectId - ID or name of the team project. Optional if specifying an ID for repository.
     */
    updateRefs(refUpdates: Git.GitRefUpdate[], repositoryId: string, project?: string, projectId?: string): Promise<Git.GitRefUpdateResult[]>;
    /**
     * Creates a ref favorite
     * 
     * @param favorite - The ref favorite to create.
     * @param project - Project ID or project name
     */
    createFavorite(favorite: Git.GitRefFavorite, project: string): Promise<Git.GitRefFavorite>;
    /**
     * Deletes the refs favorite specified
     * 
     * @param project - Project ID or project name
     * @param favoriteId - The Id of the ref favorite to delete.
     */
    deleteRefFavorite(project: string, favoriteId: number): Promise<void>;
}

class GitRestClient extends RestClientBase implements IGitRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Retrieve statistics for multiple commits
     * 
     * @param searchCriteria - Base Commit and List of Target Commits to compare.
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     */
    public async getBranchStatsBatch(
        searchCriteria: Git.GitQueryBranchStatsCriteria,
        repositoryId: string,
        project?: string
        ): Promise<Git.GitBranchStats[]> {

        return this.beginRequest<Git.GitBranchStats[]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/stats/branches",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            body: searchCriteria
        });
    }

    /**
     * Retrieve a particular commit.
     * 
     * @param commitId - The id of the commit.
     * @param repositoryId - The id or friendly name of the repository. To use the friendly name, projectId must also be specified.
     * @param project - Project ID or project name
     * @param changeCount - The number of changes to include in the result.
     */
    public async getCommit(
        commitId: string,
        repositoryId: string,
        project?: string,
        changeCount?: number
        ): Promise<Git.GitCommit> {

        const queryValues: any = {
            changeCount: changeCount
        };

        return this.beginRequest<Git.GitCommit>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/Commits/{commitId}",
            routeValues: {
                project: project,
                commitId: commitId,
                repositoryId: repositoryId
            },
            queryParams: queryValues
        });
    }

    /**
     * Retrieves pushes associated with the specified repository.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param skip - Number of pushes to skip.
     * @param top - Number of pushes to return.
     * @param searchCriteria - Search criteria attributes: fromDate, toDate, pusherId, refName, includeRefUpdates or includeLinks. fromDate: Start date to search from. toDate: End date to search to. pusherId: Identity of the person who submitted the push. refName: Branch name to consider. includeRefUpdates: If true, include the list of refs that were updated by the push. includeLinks: Whether to include the _links field on the shallow references.
     */
    public async getPushes(
        repositoryId: string,
        project?: string,
        skip?: number,
        top?: number,
        searchCriteria?: Git.GitPushSearchCriteria
        ): Promise<Git.GitPush[]> {

        const queryValues: any = {
            '$skip': skip,
            '$top': top,
            searchCriteria: searchCriteria
        };

        return this.beginRequest<Git.GitPush[]>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pushes/{pushId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues
        });
    }

    /**
     * Queries the provided repository for its refs and returns them.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param filter - [optional] A filter to apply to the refs (starts with).
     * @param includeLinks - [optional] Specifies if referenceLinks should be included in the result. default is false.
     * @param includeStatuses - [optional] Includes up to the first 1000 commit statuses for each ref. The default value is false.
     * @param includeMyBranches - [optional] Includes only branches that the user owns, the branches the user favorites, and the default branch. The default value is false. Cannot be combined with the filter parameter.
     * @param latestStatusesOnly - [optional] True to include only the tip commit status for each ref. This option requires \`includeStatuses\` to be true. The default value is false.
     * @param peelTags - [optional] Annotated tags will populate the PeeledObjectId property. default is false.
     * @param filterContains - [optional] A filter to apply to the refs (contains).
     * @param top - [optional] Maximum number of refs to return. It cannot be bigger than 1000. If it is not provided but continuationToken is, top will default to 100.
     * @param continuationToken - The continuation token used for pagination.
     */
    public async getRefs(
        repositoryId: string,
        project?: string,
        filter?: string,
        includeLinks?: boolean,
        includeStatuses?: boolean,
        includeMyBranches?: boolean,
        latestStatusesOnly?: boolean,
        peelTags?: boolean,
        filterContains?: string,
        top?: number,
        continuationToken?: string
        ): Promise<VS_Services.PagedList<Git.GitRef>> {

        const queryValues: any = {
            filter: filter,
            includeLinks: includeLinks,
            includeStatuses: includeStatuses,
            includeMyBranches: includeMyBranches,
            latestStatusesOnly: latestStatusesOnly,
            peelTags: peelTags,
            filterContains: filterContains,
            '$top': top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/refs/{*filter}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = <VS_Services.PagedList<Git.GitRef>>await response.text().then(deserializeVssJsonObject);
            body.continuationToken = response.headers.get("x-ms-continuationtoken");
            return body;
        });
    }

    /**
     * Lock or Unlock a branch.
     * 
     * @param newRefInfo - The ref update action (lock/unlock) to perform
     * @param repositoryId - The name or ID of the repository.
     * @param filter - The name of the branch to lock/unlock
     * @param project - Project ID or project name
     * @param projectId - ID or name of the team project. Optional if specifying an ID for repository.
     */
    public async updateRef(
        newRefInfo: Git.GitRefUpdate,
        repositoryId: string,
        filter: string,
        project?: string,
        projectId?: string
        ): Promise<Git.GitRef> {

        const queryValues: any = {
            filter: filter,
            projectId: projectId
        };

        return this.beginRequest<Git.GitRef>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/refs/{*filter}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            body: newRefInfo
        });
    }

    /**
     * Creating, updating, or deleting refs(branches).
     * 
     * @param refUpdates - List of ref updates to attempt to perform
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param projectId - ID or name of the team project. Optional if specifying an ID for repository.
     */
    public async updateRefs(
        refUpdates: Git.GitRefUpdate[],
        repositoryId: string,
        project?: string,
        projectId?: string
        ): Promise<Git.GitRefUpdateResult[]> {

        const queryValues: any = {
            projectId: projectId
        };

        return this.beginRequest<Git.GitRefUpdateResult[]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/refs/{*filter}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            body: refUpdates
        });
    }

    /**
     * Creates a ref favorite
     * 
     * @param favorite - The ref favorite to create.
     * @param project - Project ID or project name
     */
    public async createFavorite(
        favorite: Git.GitRefFavorite,
        project: string
        ): Promise<Git.GitRefFavorite> {

        return this.beginRequest<Git.GitRefFavorite>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/favorites/refs/{favoriteId}",
            routeValues: {
                project: project
            },
            body: favorite
        });
    }

    /**
     * Deletes the refs favorite specified
     * 
     * @param project - Project ID or project name
     * @param favoriteId - The Id of the ref favorite to delete.
     */
    public async deleteRefFavorite(
        project: string,
        favoriteId: number
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/git/favorites/refs/{favoriteId}",
            routeValues: {
                project: project,
                favoriteId: favoriteId
            }
        });
    }

}

export const GitClientName = "Repos.BranchesHub.IGitRestClient";

export function getGitClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IGitRestClient {
    return pageContext.getRestClient<IGitRestClient>(GitClientName, options);
}

RestClients.add(GitClientName, { 
    factory: GitRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
